import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useNotifier } from '../../../../components/Notifier';
import { createTireWorkorder, updateTireWorkorder } from '../../../../stores/tireWorkorderStore';
import { fleetApi } from '../../../../api/axiosClient';
import FilterBuilder from '../../../../utils/filterBuilder';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Chip,
  IconButton,
  Paper,
  Divider,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

import RepairTypeInfo from './RepairTypeInfo';
import { useLanguage } from '../../../../utils/i18n';

export default function TabTireRepair({ open, onClose, selectedTires = [], editData = null, onSuccess }) {
  const { t } = useLanguage();
  const notify = useNotifier();
  const [infoOpen, setInfoOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [useAutoWo, setUseAutoWo] = useState(true);
  const [formData, setFormData] = useState({
    vendorType: 'Internal',
    vendorName: '',
    repairType: '',
    otherRepairType: '',
    repairCost: '',
    repairDate: dayjs(),
    estimatedCompleteDate: null,
    mechanic: '',
    workOrderNumberInternal: '',
    notes: ''
  });

  const REPAIR_TYPES = [
    'Patch Repair',
    'Section Repair',
    'Bead Repair',
    'Spot Repair',
    'Retread',
    'Regrooving',
    'Tire Liquid Sealant Injection',
    'Sidewall Reinforcement',
    'Skiving and Buffing',
    'Others..'
  ];

  useEffect(() => {
    if (open && editData) {
      setUseAutoWo(!editData.work_order_number_internal);
      setFormData({
        vendorType: editData.vendor_type === 'EXTERNAL' ? 'External' : 'Internal',
        vendorName: editData.vendor_name || '',
        repairType: REPAIR_TYPES.includes(editData.repair_type) ? editData.repair_type : 'Others..',
        otherRepairType: REPAIR_TYPES.includes(editData.repair_type) ? '' : editData.repair_type || '',
        repairCost: editData.cost_repair?.toString() || '',
        repairDate: editData.repair_date ? dayjs(editData.repair_date) : dayjs(),
        estimatedCompleteDate: editData.estimated_complete_date ? dayjs(editData.estimated_complete_date) : null,
        mechanic: editData.mechanic_name || '',
        workOrderNumberInternal: editData.work_order_number_internal || '',
        notes: editData.notes || ''
      });
    } else if (open) {
      setUseAutoWo(true);
      setFormData({
        vendorType: 'Internal',
        vendorName: '',
        repairType: '',
        otherRepairType: '',
        repairCost: '',
        repairDate: dayjs(),
        estimatedCompleteDate: null,
        mechanic: '',
        workOrderNumberInternal: '',
        notes: ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editData]);

  const formatRupiah = (value) => {
    if (!value) return '';
    const number_string = value.toString().replace(/[^,\d]/g, '');
    const split = number_string.split(',');
    const sisa = split[0].length % 3;
    let rupiah = split[0].substr(0, sisa);
    const ribuan = split[0].substr(sisa).match(/\d{3}/gi);
    if (ribuan) {
      const separator = sisa ? '.' : '';
      rupiah += separator + ribuan.join('.');
    }
    return split[1] !== undefined ? rupiah + ',' + split[1] : rupiah;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'repairCost') {
      const rawValue = value.replace(/[^0-9]/g, '');
      setFormData((prev) => ({ ...prev, repairCost: rawValue }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'otherRepairType' ? value.toUpperCase() : value,
      ...(name === 'vendorType' && value === 'Internal' ? { vendorName: '' } : {}),
      ...(name === 'repairType' && value !== 'Others..' ? { otherRepairType: '' } : {})
    }));
  };

  const getCompany = () => {
    try {
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        if (parsed && (parsed.company || parsed.company_id)) {
          return parsed.company || parsed.company_id;
        }
      }
      return localStorage.getItem('companyId') || 1;
    } catch {
      return 1;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editData && !selectedTires.length) return;
    setIsSubmitting(true);

    let successfulCount = 0;
    const companyId = getCompany();

    if (editData) {
      const payload = {
        ...editData,
        work_order_number_internal: useAutoWo ? '' : formData.workOrderNumberInternal,
        vendor_type: formData.vendorType.toUpperCase(),
        vendor_name: formData.vendorType === 'Internal' ? 'Bengkel Internal' : formData.vendorName,
        repair_type: formData.repairType === 'Others..' ? formData.otherRepairType : formData.repairType,
        cost_repair: parseInt(formData.repairCost) || 0,
        repair_date: formData.repairDate ? dayjs(formData.repairDate).format('YYYY-MM-DD') : '',
        estimated_complete_date: formData.estimatedCompleteDate ? dayjs(formData.estimatedCompleteDate).format('YYYY-MM-DD') : '',
        mechanic_name: formData.mechanic,
        notes: formData.notes
      };

      try {
        await updateTireWorkorder(editData.id, payload);
        successfulCount++;
      } catch (error) {
        notify({
          message: `${t('tireRepairScrap.failedUpdateWo', 'Failed to update Work Order')}: ${error.response?.data?.message || 'Unknown error'}`,
          severity: 'error'
        });
      }
    } else {
      for (const tire of selectedTires) {
        const vehicleId = tire.last_inspection?.vehicle || tire.vehicle_id || tire.vehicle || null;
        let vehicleTypeId = null;

        if (vehicleId) {
          try {
            const builder = new FilterBuilder().setPaging(0, 1).addJoin('vehicle_type', ['name']).in('id', vehicleId.toString());

            const response = await fleetApi.post('/vehicle/search', builder.build());
            if (response?.data?.data?.[0]?.vehicle_type) {
              vehicleTypeId = parseInt(response.data.data[0].vehicle_type, 10);
            }
          } catch (error) {
            console.error('Failed to fetch vehicle type for payload:', error);
          }
        }

        const payload = {
          company_id: companyId,
          tire_item_id: tire.tire_item || tire.tire_item_id || tire.id,
          serial_number: tire.serial_number,
          tire_brand_id: tire.tire_brand_id || tire.brand_id || tire.brand || 1,
          tire_size_id: tire.tire_size_id || tire.size_id || tire.size || 1,
          tire_spec_id: tire.tire_spec_id || tire.spec_id || tire.spec || 1,
          work_order_number: useAutoWo ? '' : '',
          work_order_number_internal: useAutoWo ? '' : formData.workOrderNumberInternal,
          vendor_type: formData.vendorType.toUpperCase(),
          vendor_name: formData.vendorType === 'Internal' ? 'Bengkel Internal' : formData.vendorName,
          repair_type: formData.repairType === 'Others..' ? formData.otherRepairType : formData.repairType,
          cost_repair: parseInt(formData.repairCost) || 0,
          status: 'open',
          repair_date: formData.repairDate ? dayjs(formData.repairDate).format('YYYY-MM-DD') : '',
          estimated_complete_date: formData.estimatedCompleteDate ? dayjs(formData.estimatedCompleteDate).format('YYYY-MM-DD') : '',
          mechanic_name: formData.mechanic,
          notes: formData.notes,
          vehicle: vehicleId,
          license_plate: tire.license_plate || tire.last_vehicle || '',
          warehouse: tire.join?.tire_item_warehouse_id
            ? parseInt(tire.join.tire_item_warehouse_id, 10)
            : tire.warehouse_id || tire.warehouse || null,
          last_position: tire.last_position || tire.posCode || '',
          tire_damage_id: tire.last_inspection?.tire_damage || null,
          tire_damage_name: tire.last_inspection?.tire_damage_name || '',
          vehicle_type: vehicleTypeId || null
        };

        try {
          await createTireWorkorder(payload);
          successfulCount++;
        } catch (error) {
          notify({
            message: `${t('tireRepairScrap.failedToRepair', 'Failed to repair')} ${tire.serial_number}: ${error.response?.data?.message || 'Unknown error'}`,
            severity: 'error'
          });
        }
      }
    }

    if (successfulCount > 0) {
      notify({
        message: `${t('tireRepairScrap.successUpdateReqRepair', 'Successfully updated/requested repair for')} ${successfulCount} work order(s)`,
        severity: 'success'
      });
      if (onSuccess) {
        onSuccess();
        setIsSubmitting(false);
        return;
      }
    }
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {editData
          ? t('tireRepairScrap.editTireRepairWo', 'Edit Tire Repair Work Order')
          : t('tireRepairScrap.tireRepairForm', 'Tire Repair Form')}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
          <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {editData
                ? t('tireRepairScrap.selectedWorkOrder', 'Selected Work Order')
                : `${t('tireRepairScrap.selectedTires', 'Selected Tires')} (${selectedTires.length})`}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(editData ? [editData] : selectedTires).map((tire, idx) => (
                <Chip
                  key={tire.id || idx}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {tire.serial_number}
                      </Typography>
                      {tire.count_retread > 0 && (
                        <Typography
                          variant="caption"
                          sx={{ ml: 0.5, color: tire.count_retread >= 3 ? 'error.main' : 'info.main', fontWeight: 'bold' }}
                        >
                          ({t('tireRepairScrap.retread', 'Retread')}: {tire.count_retread}/3)
                        </Typography>
                      )}
                      <LocalShippingIcon sx={{ fontSize: 16, ml: 0.5, color: 'primary.main' }} />
                      <Typography variant="caption">{tire.license_plate || tire.last_vehicle || '-'}</Typography>
                      <Typography variant="caption" sx={{ ml: 1 }}>
                        {t('detailTireInventory.lastPosition', 'Last Position')}: {tire.last_position || '-'}
                      </Typography>
                    </Box>
                  }
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              {t('tireRepairScrap.repairDetails', 'Repair Details')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth required size="small">
                  <InputLabel>{t('tireRepairScrap.vendorType', 'Vendor Type')}</InputLabel>
                  <Select
                    name="vendorType"
                    value={formData.vendorType}
                    label={t('tireRepairScrap.vendorType', 'Vendor Type')}
                    onChange={handleChange}
                  >
                    <MenuItem value="Internal">{t('tireRepairScrap.internal', 'Internal')}</MenuItem>
                    <MenuItem value="External">{t('tireRepairScrap.external', 'External')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {formData.vendorType === 'External' && (
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    label={t('tireRepairScrap.vendorName', 'Vendor Name')}
                    name="vendorName"
                    value={formData.vendorName}
                    onChange={handleChange}
                  />
                </Grid>
              )}

              <Grid item size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl fullWidth required size="small">
                    <InputLabel>{t('tireCardHistory.repairType', 'Repair Type')}</InputLabel>
                    <Select
                      name="repairType"
                      value={formData.repairType}
                      label={t('tireCardHistory.repairType', 'Repair Type')}
                      onChange={handleChange}
                    >
                      {REPAIR_TYPES.map((type) => (
                        <MenuItem key={type} value={type.toUpperCase()}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton onClick={() => setInfoOpen(true)} size="small" color="primary" sx={{ bgcolor: 'primary.50' }}>
                    <InfoIcon />
                  </IconButton>
                </Box>
              </Grid>
              {formData.repairType === 'OTHERS..' && (
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    label={t('tireRepairScrap.otherRepairType', 'Other Repair Type')}
                    name="otherRepairType"
                    value={formData.otherRepairType}
                    onChange={handleChange}
                    helperText={t('tireRepairScrap.ensureInputCorrect', 'Ensure the input is entered correctly.')}
                  />
                </Grid>
              )}

              <Grid item size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label={t('tireRepairScrap.repairCost', 'Repair Cost')}
                  name="repairCost"
                  value={formatRupiah(formData.repairCost)}
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'success.50',
                      '&.Mui-focused': { bgcolor: 'success.100' }
                    }
                  }}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary', fontWeight: 'bold' }}>Rp</Typography>
                  }}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label={t('tireRepairScrap.repairDate', 'Repair Date')}
                    format="DD/MM/YYYY"
                    value={formData.repairDate}
                    onChange={(newVal) => {
                      setFormData((prev) => {
                        const newState = { ...prev, repairDate: newVal };
                        if (prev.estimatedCompleteDate && newVal && prev.estimatedCompleteDate.isBefore(newVal, 'day')) {
                          newState.estimatedCompleteDate = newVal;
                        }
                        return newState;
                      });
                    }}
                    slotProps={{
                      textField: { fullWidth: true, size: 'small', required: true }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label={t('tireRepairScrap.estCompleteDate', 'Estimated Complete Date')}
                    format="DD/MM/YYYY"
                    minDate={formData.repairDate}
                    value={formData.estimatedCompleteDate}
                    onChange={(newVal) => setFormData((prev) => ({ ...prev, estimatedCompleteDate: newVal }))}
                    slotProps={{
                      textField: { fullWidth: true, size: 'small' }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label={t('tireRepairScrap.mechanicName', 'Mechanic Name')}
                  name="mechanic"
                  value={formData.mechanic}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <FormControlLabel
                    control={<Checkbox checked={useAutoWo} onChange={(e) => setUseAutoWo(e.target.checked)} color="primary" />}
                    label={t('tireRepairScrap.useAutoWo', 'Use Generate Automatic WO Number')}
                  />
                  {!useAutoWo && (
                    <TextField
                      fullWidth
                      required
                      size="small"
                      label={t('tireRepairScrap.woInternal', 'WO Internal')}
                      name="workOrderNumberInternal"
                      value={formData.workOrderNumberInternal}
                      onChange={handleChange}
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              </Grid>
              <Grid item size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('tireRepairScrap.notes', 'Notes')}
                  name="notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined" color="inherit" disabled={isSubmitting}>
            {t('general.cancel', 'Cancel')}
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
            {isSubmitting ? t('general.submitting', 'Submitting...') : t('tireRepairScrap.submitRepair', 'Submit Repair')}
          </Button>
        </DialogActions>
      </form>
      <RepairTypeInfo open={infoOpen} onClose={() => setInfoOpen(false)} />
    </Dialog>
  );
}
